import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  PenLine, Heart, Brain, Sparkles, Send, Eye, EyeOff,
  MessageCircle, ChevronDown, Feather, Shield, Sun, Moon,
  Cloud, Quote, BookOpen, ExternalLink, Check, Users, Lock,
  Info, ArrowRight,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/* ============================================================
   IMAGES — positive, reassuring, empathic
   ============================================================ */
const IMAGES = {
  notebook: "https://static.prod-images.emergentagent.com/jobs/ee7d5fa0-204e-41a0-8d1b-3a1a63362d7a/images/8feae394bfa76fb57b4cde8b5ec81e72147fd8acab3562b4176c10fbf9f2f900.png",
  heart: "https://static.prod-images.emergentagent.com/jobs/ee7d5fa0-204e-41a0-8d1b-3a1a63362d7a/images/2cb68d61558377cbd1732c7328401b881b0d8b901dc08bf133e9cf44f2e802c1.png",
  science: "https://static.prod-images.emergentagent.com/jobs/ee7d5fa0-204e-41a0-8d1b-3a1a63362d7a/images/3035ebcd78a6f6b4ab632527ef2e6bb16d516b20613c93224d9a1e534dec829a.png",
  hope: "https://static.prod-images.emergentagent.com/jobs/ee7d5fa0-204e-41a0-8d1b-3a1a63362d7a/images/821d7257f9ef877b6f6ae6676a91dee188679dc60f2825485ee97d6bc0751ac9.png",
  writing: "https://images.unsplash.com/photo-1701634441311-e624c7fdfedf?w=800&q=80",
  community: "https://images.unsplash.com/photo-1745962981417-45b9da55456d?w=800&q=80",
  nature: "https://images.unsplash.com/photo-1774462367455-b5a0e942384b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxzb2Z0JTIwbmF0dXJlJTIwc3VubGlnaHQlMjBwZWFjZWZ1bCUyMGNhbG18ZW58MHx8fHwxNzc1MjEzMjA1fDA&ixlib=rb-4.1.0&q=85",
  flowers: "https://images.unsplash.com/photo-1765526474510-b48265e14f3c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHxzb2Z0JTIwcGluayUyMGZsb3dlcnMlMjBwYXN0ZWwlMjBnZW50bGV8ZW58MHx8fHwxNzc1MjEzMjA1fDA&ixlib=rb-4.1.0&q=85",
  warmth: "https://images.unsplash.com/photo-1764867179313-6c02c12c6f6d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHx3YXJtJTIwY296eSUyMHNvZnQlMjBsaWdodCUyMHBlYWNlZnVsfGVufDB8fHx8MTc3NTIxMzIwNXww&ixlib=rb-4.1.0&q=85",
  serenity: "https://images.unsplash.com/photo-1677011448746-58c36a985dcd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwyfHxzZXJlbmUlMjBvY2VhbiUyMGNhbG0lMjBob3Jpem9uJTIwaG9wZXxlbnwwfHx8fDE3NzUyMTMyMDV8MA&ixlib=rb-4.1.0&q=85",
  smile: "https://images.pexels.com/photos/13821256/pexels-photo-13821256.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

/* ============================================================
   SCIENTIFIC REFERENCES — real PubMed studies
   ============================================================ */
const SCIENCE_REFS = [
  {
    icon: Brain,
    title: "Riduce stress e ansia",
    desc: "La scrittura espressiva abbassa i livelli di cortisolo e attiva aree cerebrali legate alla regolazione emotiva.",
    study: "Pennebaker, J.W. & Beall, S.K. (1986). Confronting a traumatic event: Toward an understanding of inhibition and disease.",
    journal: "Journal of Abnormal Psychology, 95(3), 274-281",
    doi: "10.1037/0021-843X.95.3.274",
  },
  {
    icon: Heart,
    title: "Rielabora i traumi",
    desc: "Scrivere dei propri traumi per 15-20 minuti al giorno migliora la salute fisica e mentale in modo misurabile.",
    study: "Pennebaker, J.W. (1997). Writing about emotional experiences as a therapeutic process.",
    journal: "Psychological Science, 8(3), 162-166",
    doi: "10.1111/j.1467-9280.1997.tb00403.x",
  },
  {
    icon: Feather,
    title: "Chiarezza mentale",
    desc: "La narrazione scritta organizza i pensieri caotici in strutture coerenti, favorendo l'elaborazione cognitiva.",
    study: "Smyth, J.M. (1998). Written emotional expression: Effect sizes, outcome types, and moderating variables.",
    journal: "Journal of Consulting and Clinical Psychology, 66(1), 174-184",
    doi: "10.1037/0022-006X.66.1.174",
  },
  {
    icon: Shield,
    title: "Supporto in oncologia",
    desc: "In pazienti oncologici, la scrittura terapeutica riduce ansia, migliora la qualita della vita e rafforza la resilienza.",
    study: "Stanton, A.L. et al. (2002). Emotionally expressive coping predicts psychological and physical adjustment to breast cancer.",
    journal: "Journal of Consulting and Clinical Psychology, 70(3), 875-882",
    doi: "10.1037/0022-006X.70.3.875",
  },
];

/* ============================================================
   THEMES
   ============================================================ */
const THEMES = {
  luce: { bg: "#FCFAF9", card: "rgba(255,255,255,0.6)", text: "#2D2426", muted: "#736366", accent: "#D5A3AC", accentHover: "#B87A88", lilac: "#B39DDB", softBg: "#F4F0FA", pinkBg: "#FCEEF0", label: "Luce", icon: Sun },
  calma: { bg: "#F4F0FA", card: "rgba(179,157,219,0.1)", text: "#2D2426", muted: "#736366", accent: "#9575CD", accentHover: "#7E57C2", lilac: "#B39DDB", softBg: "#EDE7F6", pinkBg: "#F3E5F5", label: "Calma", icon: Cloud },
  notte: { bg: "#1A1520", card: "rgba(45,36,38,0.6)", text: "#F5EEF0", muted: "#A89CA0", accent: "#D5A3AC", accentHover: "#E8BFC6", lilac: "#CE93D8", softBg: "#2D2430", pinkBg: "#2A1E28", label: "Notte", icon: Moon },
};

/* ============================================================
   SAMPLE STORIES
   ============================================================ */
const SAMPLE_STORIES = [
  { story_id: "s1", text: "Dopo la mastectomia, non riuscivo a guardarmi allo specchio. Un giorno mia figlia di 6 anni mi ha abbracciata e ha detto: 'Mamma, sei bellissima perche sei forte.' Quel giorno ho ricominciato a scrivere. Prima una parola, poi due, poi pagine intere. La scrittura mi ha restituito la mia voce.", author_name: "Maria, 42 anni", is_anonymous: false, helped_count: 127, word_count: 52 },
  { story_id: "s2", text: "Ho scoperto di avere un linfoma a 28 anni. Il mondo si e fermato. Ma poi ho iniziato a scrivere lettere al mio corpo, come mi ha suggerito la psicologa. 'Caro corpo, oggi ti chiedo scusa per tutte le volte che non ti ho ascoltato.' Scrivere e diventato il mio rifugio.", author_name: null, is_anonymous: true, helped_count: 89, word_count: 49 },
  { story_id: "s3", text: "Tre cicli di chemio. Capelli persi, forza persa, speranza quasi persa. Poi una sera ho scritto: 'Domani il sole sorgera anche per me.' L'ho appeso allo specchio. Ogni mattina lo leggevo. Oggi sono qui, in remissione, e quella frase e ancora li. Le parole salvano.", author_name: "Giulia", is_anonymous: false, helped_count: 203, word_count: 45 },
  { story_id: "s4", text: "Non riesco ancora a parlarne a voce alta. Ma qui, scrivendo, riesco a dire che ho paura. Ho paura del prossimo controllo. Ho paura che torni. Ma scrivere mi fa sentire meno sola, come se qualcuno dall'altra parte stesse leggendo e capendo.", author_name: null, is_anonymous: true, helped_count: 156, word_count: 41 },
];

/* ============================================================
   MAIN COMPONENT
   ============================================================ */
export default function OdrStoriesPage() {
  const [theme, setTheme] = useState("luce");
  const [dailyContent, setDailyContent] = useState(null);
  const [loadingDaily, setLoadingDaily] = useState(true);
  const [storyText, setStoryText] = useState("");
  const [isAnonymous, setIsAnonymous] = useState(true);
  const [authorName, setAuthorName] = useState("");
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [enhancedResult, setEnhancedResult] = useState(null);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [chatSessionId, setChatSessionId] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [stats, setStats] = useState({ total_stories: 247, total_words: 18420, total_helped: 891 });
  const [stories, setStories] = useState(SAMPLE_STORIES);
  const writingRef = useRef(null);
  const chatEndRef = useRef(null);
  const navigate = useNavigate();
  const t = THEMES[theme];

  useEffect(() => { fetchDailyContent(); fetchStats(); fetchStories(); }, []);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [chatMessages]);

  const fetchDailyContent = async () => {
    try { const res = await fetch(`${API}/api/stories/daily-content`); if (res.ok) setDailyContent(await res.json()); } catch (e) {/* fallback */}
    setLoadingDaily(false);
  };
  const fetchStats = async () => {
    try { const res = await fetch(`${API}/api/stories/stats`); if (res.ok) { const d = await res.json(); if (d.total_stories > 0) setStats(d); } } catch (e) {/* keep defaults */}
  };
  const fetchStories = async () => {
    try { const res = await fetch(`${API}/api/stories/feed`); if (res.ok) { const d = await res.json(); if (d.stories.length > 0) setStories(d.stories); } } catch (e) {/* keep samples */}
  };
  const handleEnhance = async () => {
    if (!storyText.trim() || isEnhancing) return;
    setIsEnhancing(true);
    try { const res = await fetch(`${API}/api/stories/enhance`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text: storyText }) }); if (res.ok) setEnhancedResult(await res.json()); } catch (e) {/* silent */}
    setIsEnhancing(false);
  };
  const handleSaveStory = async () => {
    if (!storyText.trim() || isSaving || !privacyAccepted) return;
    setIsSaving(true);
    try { const res = await fetch(`${API}/api/stories/write`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text: storyText, is_anonymous: isAnonymous, author_name: isAnonymous ? null : authorName }) }); if (res.ok) { setSaved(true); fetchStats(); fetchStories(); } } catch (e) {/* silent */}
    setIsSaving(false);
  };
  const handleChat = async () => {
    if (!chatInput.trim() || chatLoading) return;
    const userMsg = chatInput.trim();
    setChatInput("");
    setChatMessages(prev => [...prev, { role: "user", text: userMsg }]);
    setChatLoading(true);
    try { const res = await fetch(`${API}/api/stories/chat`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ message: userMsg, session_id: chatSessionId }) }); if (res.ok) { const d = await res.json(); setChatSessionId(d.session_id); setChatMessages(prev => [...prev, { role: "ai", text: d.response }]); } } catch (e) { setChatMessages(prev => [...prev, { role: "ai", text: "Sono qui con te. Prenditi il tempo che ti serve." }]); }
    setChatLoading(false);
  };
  const handleHelped = async (storyId) => {
    try { await fetch(`${API}/api/stories/helped`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ story_id: storyId }) }); setStories(prev => prev.map(s => s.story_id === storyId ? { ...s, helped_count: (s.helped_count || 0) + 1 } : s)); setStats(prev => ({ ...prev, total_helped: prev.total_helped + 1 })); } catch (e) {/* silent */}
  };
  const scrollToWriting = () => { writingRef.current?.scrollIntoView({ behavior: "smooth" }); };
  const wordCount = storyText.trim() ? storyText.trim().split(/\s+/).length : 0;

  const fadeUp = { hidden: { opacity: 0, y: 40 }, visible: { opacity: 1, y: 0, transition: { duration: 0.9, ease: "easeOut" } } };
  const stagger = { visible: { transition: { staggerChildren: 0.15 } } };

  return (
    <div className="min-h-screen transition-colors duration-1000" style={{ backgroundColor: t.bg, color: t.text, fontFamily: "'Manrope', sans-serif" }} data-testid="odr-stories-page">
      <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&family=Manrope:wght@300;400;500;600&display=swap" rel="stylesheet" />

      {/* ============ 1. HERO ============ */}
      <section className="relative min-h-screen flex flex-col items-center justify-center px-6 overflow-hidden" data-testid="hero-section">
        {/* Background orbs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute w-[500px] h-[500px] rounded-full blur-3xl opacity-20" style={{ background: t.lilac, top: "-10%", left: "-10%", animation: "float1 20s ease-in-out infinite" }} />
          <div className="absolute w-[400px] h-[400px] rounded-full blur-3xl opacity-15" style={{ background: t.accent, bottom: "-5%", right: "-5%", animation: "float2 25s ease-in-out infinite" }} />
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 lg:px-20 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center py-20">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.div variants={fadeUp} className="mb-6"><img src={IMAGES.heart} alt="" className="w-14 h-14 object-contain opacity-80" /></motion.div>
            <motion.p variants={fadeUp} className="text-sm tracking-[0.3em] uppercase mb-5" style={{ color: t.accent }}>ODR Stories</motion.p>
            <motion.h1 variants={fadeUp} className="text-4xl sm:text-5xl lg:text-[3.5rem] leading-[1.15] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>La tua storia puo aiutare qualcuno a non sentirsi solo</motion.h1>
            <motion.p variants={fadeUp} className="text-lg leading-relaxed mb-10 max-w-lg" style={{ color: t.muted }}>E se non trovi le parole, ti aiutiamo a trovarle. Qui ogni parola diventa forza.</motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap gap-4">
              <button onClick={scrollToWriting} className="inline-flex items-center gap-3 px-9 py-4 rounded-full text-white font-medium transition-all duration-700 hover:-translate-y-1 hover:shadow-xl" style={{ backgroundColor: t.accent, boxShadow: `0 6px 24px ${t.accent}35` }} data-testid="hero-cta-btn"><PenLine className="w-5 h-5" /> Inizia a scrivere</button>
              <button onClick={() => navigate("/stories/community")} className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-medium border-2 transition-all duration-700 hover:-translate-y-1" style={{ borderColor: `${t.accent}50`, color: t.accent }} data-testid="hero-community-btn"><BookOpen className="w-5 h-5" /> Leggi le storie</button>
            </motion.div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.2, delay: 0.3 }} className="hidden lg:block">
            <div className="rounded-[2rem] overflow-hidden shadow-2xl" style={{ boxShadow: `0 20px 60px ${t.accent}20` }}><img src={IMAGES.hope} alt="Speranza e luce" className="w-full h-[500px] object-cover" /></div>
          </motion.div>
        </div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2 }} className="absolute bottom-12">
          <ChevronDown className="w-6 h-6 animate-bounce" style={{ color: t.muted }} />
        </motion.div>
      </section>

      {/* ============ 2. AI VIVA ============ */}
      <section className="px-6 md:px-12 lg:px-24 py-24 lg:py-32" data-testid="ai-viva-section">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger} className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
          <motion.div variants={fadeUp}>
            {/* Image */}
            <div className="rounded-3xl overflow-hidden mb-8 shadow-lg" style={{ boxShadow: `0 12px 40px ${t.accent}15` }}>
              <img src={IMAGES.nature} alt="Natura e serenita" className="w-full h-64 object-cover" />
            </div>
            <p className="text-sm tracking-[0.2em] uppercase mb-4" style={{ color: t.lilac }}>AI Viva</p>
            <h2 className="text-3xl sm:text-4xl mb-6 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              Ogni giorno, una voce che ispira
            </h2>
            <p className="text-base leading-relaxed mb-4" style={{ color: t.muted }}>
              Ogni mattina, una presenza gentile genera storie empatiche, riflessioni e messaggi di forza.
              Non contenuti automatici. <em>Presenze che accompagnano.</em>
            </p>
          </motion.div>

          <motion.div variants={fadeUp}>
            <div className="rounded-3xl p-8 md:p-10 backdrop-blur-2xl border transition-all duration-700" style={{ background: t.card, borderColor: "rgba(255,255,255,0.4)", boxShadow: `0 8px 32px ${t.accent}10` }}>
              {loadingDaily ? (
                <div className="flex items-center gap-3" style={{ color: t.muted }}>
                  <Sparkles className="w-5 h-5 animate-pulse" /><span className="italic">Generando ispirazione...</span>
                </div>
              ) : (
                <div className="space-y-6">
                  <div>
                    <p className="text-xs uppercase tracking-wider mb-3" style={{ color: t.lilac }}>Riflessione del giorno</p>
                    <p className="text-base leading-relaxed italic" style={{ fontFamily: "'Playfair Display', serif" }}>
                      "{dailyContent?.reflection || "La forza non e assenza di fragilita, ma la capacita di attraversarla."}"
                    </p>
                  </div>
                  <div className="h-px w-full" style={{ background: `linear-gradient(to right, transparent, ${t.accent}40, transparent)` }} />
                  <div className="flex items-start gap-3">
                    <Sparkles className="w-5 h-5 mt-1 flex-shrink-0" style={{ color: t.accent }} />
                    <p className="font-medium text-lg" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {dailyContent?.mantra || "La tua luce esiste anche quando non la vedi."}
                    </p>
                  </div>
                  {dailyContent?.message && (
                    <>
                      <div className="h-px w-full" style={{ background: `linear-gradient(to right, transparent, ${t.accent}40, transparent)` }} />
                      <p className="text-sm leading-relaxed" style={{ color: t.muted }}>{dailyContent.message}</p>
                    </>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* ============ 3. SCIENTIFIC SECTION ============ */}
      <section className="px-6 md:px-12 lg:px-24 py-24 lg:py-32 transition-colors duration-1000" style={{ backgroundColor: t.softBg }} data-testid="scientific-section">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger} className="max-w-6xl mx-auto">
          <motion.div variants={fadeUp} className="text-center mb-16">
            <p className="text-sm tracking-[0.2em] uppercase mb-4" style={{ color: t.lilac }}>Evidenze scientifiche</p>
            <h2 className="text-3xl sm:text-4xl mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Scrivere fa bene. La scienza lo dimostra.
            </h2>
            <p className="text-base max-w-2xl mx-auto" style={{ color: t.muted }}>
              Decenni di ricerche peer-reviewed confermano i benefici della scrittura espressiva sulla salute psicofisica.
            </p>
          </motion.div>

          {/* Evocative image divider */}
          <motion.div variants={fadeUp} className="rounded-3xl overflow-hidden mb-16 max-w-3xl mx-auto shadow-lg" style={{ boxShadow: `0 12px 40px ${t.accent}10` }}>
            <img src={IMAGES.flowers} alt="Fiori e rinascita" className="w-full h-48 object-cover" />
          </motion.div>

          <div className="grid sm:grid-cols-2 gap-6">
            {SCIENCE_REFS.map((item, i) => (
              <motion.div key={i} variants={fadeUp} className="rounded-3xl p-8 transition-all duration-700 hover:-translate-y-1" style={{ background: t.card, backdropFilter: "blur(24px)", border: `1px solid rgba(255,255,255,0.4)`, boxShadow: `0 4px 20px ${t.accent}08` }}>
                <item.icon className="w-8 h-8 mb-5" style={{ color: t.accent }} strokeWidth={1.5} />
                <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                <p className="text-sm leading-relaxed mb-4" style={{ color: t.muted }}>{item.desc}</p>
                {/* Scientific reference */}
                <div className="pt-4 mt-4" style={{ borderTop: `1px solid ${t.text}08` }}>
                  <p className="text-xs leading-relaxed" style={{ color: t.muted }}>
                    <BookOpen className="w-3 h-3 inline mr-1" style={{ color: t.lilac }} />
                    {item.study}
                  </p>
                  <p className="text-xs mt-1 italic" style={{ color: t.lilac }}>
                    {item.journal}
                  </p>
                  <a href={`https://doi.org/${item.doi}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs mt-2 transition-opacity hover:opacity-70" style={{ color: t.accent }}>
                    DOI: {item.doi} <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* ============ 4. AI INTERACTION & CHAT ============ */}
      <section className="px-6 md:px-12 lg:px-24 py-24 lg:py-32" data-testid="ai-interaction-section">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger} className="max-w-3xl mx-auto text-center">
          <motion.div variants={fadeUp}>
            {/* Empathic image */}
            <div className="w-24 h-24 mx-auto mb-8 rounded-full overflow-hidden shadow-lg" style={{ boxShadow: `0 8px 30px ${t.lilac}30` }}>
              <img src={IMAGES.warmth} alt="Calore e connessione" className="w-full h-full object-cover" />
            </div>
            <p className="text-sm tracking-[0.2em] uppercase mb-4" style={{ color: t.lilac }}>Assistente Empatico</p>
            <h2 className="text-3xl sm:text-4xl mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Se non trovi le parole, iniziamo insieme
            </h2>
            <p className="text-base mb-10" style={{ color: t.muted }}>Non devi sapere scrivere. Devi solo iniziare.</p>
          </motion.div>

          <motion.div variants={fadeUp} className="flex flex-wrap justify-center gap-3 mb-8">
            {["Vuoi iniziare da qui?", "Raccontami un momento difficile...", "Cosa diresti a te stessa oggi?", "Scrivi una lettera al tuo corpo"].map((prompt, i) => (
              <button key={i} onClick={() => { setChatOpen(true); setChatInput(prompt); }} className="px-5 py-2.5 rounded-full text-sm border transition-all duration-500 hover:-translate-y-0.5" style={{ borderColor: `${t.accent}40`, color: t.accent, background: `${t.accent}08` }} data-testid={`chat-prompt-${i}`}>
                {prompt}
              </button>
            ))}
          </motion.div>

          {!chatOpen && (
            <motion.button variants={fadeUp} onClick={() => setChatOpen(true)} className="inline-flex items-center gap-3 px-8 py-4 rounded-full text-white font-medium transition-all duration-700 hover:-translate-y-1" style={{ backgroundColor: t.lilac, boxShadow: `0 4px 20px ${t.lilac}30` }} data-testid="open-chat-btn">
              <MessageCircle className="w-5 h-5" /> Parla con me
            </motion.button>
          )}

          <AnimatePresence>
            {chatOpen && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }} className="mt-8 rounded-3xl overflow-hidden text-left" style={{ background: t.card, backdropFilter: "blur(24px)", border: `1px solid rgba(255,255,255,0.3)`, boxShadow: `0 8px 40px rgba(0,0,0,0.06)` }} data-testid="chat-panel">
                <div className="p-6 max-h-80 overflow-y-auto space-y-4" style={{ scrollbarWidth: "none" }}>
                  {chatMessages.length === 0 && (
                    <div className="text-center py-8">
                      <Sparkles className="w-8 h-8 mx-auto mb-3" style={{ color: t.accent }} />
                      <p className="text-sm italic" style={{ color: t.muted }}>Sono qui con te. Prenditi il tempo che ti serve.</p>
                    </div>
                  )}
                  {chatMessages.map((msg, i) => (
                    <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                      <div className="max-w-[80%] px-5 py-3 rounded-2xl text-sm leading-relaxed" style={{ backgroundColor: msg.role === "user" ? t.accent : t.softBg, color: msg.role === "user" ? "#fff" : t.text }}>{msg.text}</div>
                    </div>
                  ))}
                  {chatLoading && <div className="flex justify-start"><div className="px-5 py-3 rounded-2xl text-sm" style={{ backgroundColor: t.softBg }}><span className="animate-pulse">...</span></div></div>}
                  <div ref={chatEndRef} />
                </div>
                <div className="p-4 border-t" style={{ borderColor: `${t.text}10` }}>
                  <div className="flex gap-3">
                    <input value={chatInput} onChange={e => setChatInput(e.target.value)} onKeyDown={e => e.key === "Enter" && handleChat()} placeholder="Scrivi qui..." className="flex-1 px-5 py-3 rounded-full text-sm outline-none transition-all duration-500" style={{ backgroundColor: t.softBg, color: t.text, border: `1px solid ${t.accent}20` }} data-testid="chat-input" />
                    <button onClick={handleChat} disabled={chatLoading || !chatInput.trim()} className="w-11 h-11 rounded-full flex items-center justify-center text-white transition-all duration-500 disabled:opacity-40" style={{ backgroundColor: t.accent }} data-testid="chat-send-btn"><Send className="w-4 h-4" /></button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </section>

      {/* ============ 5. WRITING SECTION ============ */}
      <section ref={writingRef} className="px-6 md:px-12 lg:px-24 py-24 lg:py-32 transition-colors duration-1000" style={{ backgroundColor: t.pinkBg }} data-testid="writing-section">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger} className="max-w-3xl mx-auto">
          <motion.div variants={fadeUp} className="text-center mb-12">
            <p className="text-sm tracking-[0.2em] uppercase mb-4" style={{ color: t.accent }}>Il tuo spazio sicuro</p>
            <h2 className="text-3xl sm:text-4xl mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Scrivi la tua storia</h2>
            <p className="text-base" style={{ color: t.muted }}>Non devi essere perfetta. Devi solo essere vera.</p>
          </motion.div>

          {!saved ? (
            <motion.div variants={fadeUp}>
              {/* Writing area */}
              <div className="rounded-3xl p-8 md:p-12 mb-6 transition-all duration-700" style={{ background: t.bg, boxShadow: `inset 0 2px 10px rgba(0,0,0,0.02)`, border: `1px solid ${t.text}08` }}>
                <textarea value={storyText} onChange={e => setStoryText(e.target.value)} placeholder="Inizia a scrivere qui... anche solo una parola." rows={8} className="w-full bg-transparent border-none outline-none text-lg md:text-xl leading-relaxed resize-none" style={{ color: t.text, fontFamily: "'Playfair Display', serif", scrollbarWidth: "none" }} data-testid="story-textarea" />
                <div className="flex items-center justify-between mt-4 pt-4" style={{ borderTop: `1px solid ${t.text}08` }}>
                  <span className="text-xs" style={{ color: t.muted }}>{wordCount} {wordCount === 1 ? "parola" : "parole"}</span>
                  <button onClick={() => setIsAnonymous(!isAnonymous)} className="flex items-center gap-2 text-xs transition-colors duration-300" style={{ color: t.muted }} data-testid="anonymous-toggle">
                    {isAnonymous ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    {isAnonymous ? "Anonimo" : "Con nome"}
                  </button>
                </div>
                {!isAnonymous && (
                  <input value={authorName} onChange={e => setAuthorName(e.target.value)} placeholder="Il tuo nome (opzionale)" className="mt-3 w-full bg-transparent border-none outline-none text-sm" style={{ color: t.muted }} data-testid="author-name-input" />
                )}
              </div>

              {/* Privacy consent */}
              <div className="mb-6 rounded-2xl p-5" style={{ background: `${t.softBg}80`, border: `1px solid ${t.text}06` }}>
                <label className="flex items-start gap-3 cursor-pointer" data-testid="privacy-checkbox-label">
                  <div className="relative mt-0.5 flex-shrink-0">
                    <input type="checkbox" checked={privacyAccepted} onChange={e => setPrivacyAccepted(e.target.checked)} className="sr-only" data-testid="privacy-checkbox" />
                    <div className="w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all duration-300" style={{ borderColor: privacyAccepted ? t.accent : `${t.text}25`, backgroundColor: privacyAccepted ? t.accent : "transparent" }}>
                      {privacyAccepted && <Check className="w-3 h-3 text-white" />}
                    </div>
                  </div>
                  <span className="text-sm leading-relaxed" style={{ color: t.muted }}>
                    <Lock className="w-3.5 h-3.5 inline mr-1" style={{ color: t.lilac }} />
                    Acconsento al trattamento dei miei dati personali ai sensi del GDPR (Reg. UE 2016/679). La mia storia potra essere condivisa in forma {isAnonymous ? "anonima" : "con il mio nome"} per ispirare e supportare altre persone. Posso richiedere la cancellazione in qualsiasi momento.
                  </span>
                </label>
              </div>

              {/* Buttons */}
              <div className="flex flex-wrap gap-4 justify-center">
                <button onClick={handleEnhance} disabled={!storyText.trim() || isEnhancing} className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-sm font-medium border-2 transition-all duration-700 hover:-translate-y-1 disabled:opacity-40" style={{ borderColor: t.accent, color: t.accent }} data-testid="enhance-btn">
                  <Sparkles className="w-4 h-4" /> {isEnhancing ? "Sto leggendo..." : "Valorizza le mie parole"}
                </button>
                <button onClick={handleSaveStory} disabled={!storyText.trim() || isSaving || !privacyAccepted} className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-sm font-medium text-white transition-all duration-700 hover:-translate-y-1 disabled:opacity-40" style={{ backgroundColor: t.accent, boxShadow: privacyAccepted ? `0 4px 20px ${t.accent}30` : "none" }} data-testid="save-story-btn">
                  <Heart className="w-4 h-4" /> {isSaving ? "Salvataggio..." : "Condividi la tua storia"}
                </button>
              </div>
              {!privacyAccepted && storyText.trim() && (
                <p className="text-center text-xs mt-3" style={{ color: t.accent }}>
                  Accetta la privacy per poter condividere la tua storia
                </p>
              )}

              {/* Enhanced result */}
              <AnimatePresence>
                {enhancedResult && (
                  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-10 rounded-3xl p-8 md:p-10 space-y-6" style={{ background: t.card, backdropFilter: "blur(24px)", border: `1px solid rgba(255,255,255,0.3)` }} data-testid="enhanced-result">
                    <div>
                      <p className="text-xs uppercase tracking-wider mb-3" style={{ color: t.lilac }}>La tua storia, valorizzata</p>
                      <p className="text-base leading-relaxed italic" style={{ fontFamily: "'Playfair Display', serif" }}>"{enhancedResult.enhanced_text}"</p>
                    </div>
                    <div style={{ background: `linear-gradient(to right, transparent, ${t.accent}30, transparent)`, height: 1 }} />
                    <div className="text-center">
                      <p className="text-xs uppercase tracking-wider mb-2" style={{ color: t.accent }}>Il tuo mantra personale</p>
                      <p className="text-xl font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>{enhancedResult.mantra}</p>
                    </div>
                    <div style={{ background: `linear-gradient(to right, transparent, ${t.accent}30, transparent)`, height: 1 }} />
                    <p className="text-sm leading-relaxed text-center" style={{ color: t.muted }}>{enhancedResult.encouragement}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ) : (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-12">
              <Heart className="w-12 h-12 mx-auto mb-6" style={{ color: t.accent }} />
              <h3 className="text-2xl mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Grazie per aver condiviso</h3>
              <p className="text-base" style={{ color: t.muted }}>Le tue parole possono aiutare qualcuno oggi. E questo e un atto di coraggio.</p>
              <button onClick={() => { setSaved(false); setStoryText(""); setEnhancedResult(null); setPrivacyAccepted(false); }} className="mt-8 px-8 py-3 rounded-full text-sm border-2 transition-all duration-700" style={{ borderColor: t.accent, color: t.accent }} data-testid="write-another-btn">Scrivi un'altra storia</button>
            </motion.div>
          )}
        </motion.div>
      </section>

      {/* ============ 5b. DOVE FINISCONO LE TUE STORIE ============ */}
      <section className="px-6 md:px-12 lg:px-24 py-24 lg:py-32" data-testid="where-stories-go-section">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger} className="max-w-4xl mx-auto">
          <motion.div variants={fadeUp} className="text-center mb-12">
            <p className="text-sm tracking-[0.2em] uppercase mb-4" style={{ color: t.lilac }}>Trasparenza</p>
            <h2 className="text-3xl sm:text-4xl mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Dove finiscono le tue storie
            </h2>
          </motion.div>

          <motion.div variants={fadeUp} className="grid sm:grid-cols-3 gap-6">
            {[
              { icon: Users, title: "Community empatica", desc: "Le storie condivise diventano parte di uno spazio sicuro dove altre persone possono ritrovarsi, sentirsi meno sole e trovare ispirazione." },
              { icon: Lock, title: "Privacy protetta", desc: "Ogni storia puo essere condivisa in forma completamente anonima. I tuoi dati sono protetti dal GDPR e puoi richiederne la cancellazione in ogni momento." },
              { icon: Heart, title: "Impatto reale", desc: "Quando qualcuno legge la tua storia e clicca 'Mi ha aiutato', ricevi conferma che le tue parole stanno facendo la differenza nella vita di qualcuno." },
            ].map((item, i) => (
              <div key={i} className="rounded-3xl p-8 text-center transition-all duration-700" style={{ background: t.card, backdropFilter: "blur(24px)", border: `1px solid rgba(255,255,255,0.3)` }}>
                <div className="w-14 h-14 mx-auto mb-5 rounded-full flex items-center justify-center" style={{ backgroundColor: `${t.accent}12` }}>
                  <item.icon className="w-6 h-6" style={{ color: t.accent }} strokeWidth={1.5} />
                </div>
                <h3 className="text-base font-semibold mb-3">{item.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: t.muted }}>{item.desc}</p>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </section>

      {/* ============ 6. HUMAN IMPACT ============ */}
      <section className="px-6 md:px-12 lg:px-24 py-24 lg:py-32 transition-colors duration-1000" style={{ backgroundColor: t.softBg }} data-testid="impact-section">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger} className="max-w-5xl mx-auto">
          <motion.div variants={fadeUp} className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Le tue parole possono aiutare qualcuno oggi</h2>
            <p className="text-base" style={{ color: t.muted }}>Ogni storia condivisa e un atto di generosita</p>
          </motion.div>

          {/* Evocative image */}
          <motion.div variants={fadeUp} className="rounded-3xl overflow-hidden mb-16 max-w-2xl mx-auto shadow-lg" style={{ boxShadow: `0 12px 40px ${t.accent}10` }}>
            <img src={IMAGES.serenity} alt="Serenita e speranza" className="w-full h-56 object-cover" />
          </motion.div>

          {/* Counters */}
          <motion.div variants={fadeUp} className="grid grid-cols-3 gap-6 mb-16">
            {[
              { value: stats.total_stories, label: "Storie condivise" },
              { value: stats.total_words, label: "Parole scritte" },
              { value: stats.total_helped, label: "Persone aiutate" },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <p className="text-3xl sm:text-4xl font-bold mb-2" style={{ color: t.accent, fontFamily: "'Playfair Display', serif" }}>{stat.value.toLocaleString("it-IT")}</p>
                <p className="text-sm" style={{ color: t.muted }}>{stat.label}</p>
              </div>
            ))}
          </motion.div>

          {/* Stories feed */}
          <div className="space-y-6">
            {stories.slice(0, 4).map((story, i) => (
              <motion.div key={story.story_id} variants={fadeUp} className="rounded-3xl p-8 transition-all duration-700 hover:-translate-y-0.5" style={{ background: t.card, backdropFilter: "blur(24px)", border: `1px solid rgba(255,255,255,0.3)`, boxShadow: `0 4px 20px rgba(0,0,0,0.03)` }} data-testid={`story-card-${i}`}>
                <div className="flex items-start gap-4">
                  <Quote className="w-8 h-8 flex-shrink-0 mt-1" style={{ color: `${t.lilac}60` }} strokeWidth={1.5} />
                  <div className="flex-1">
                    <p className="text-base leading-relaxed mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>{story.text}</p>
                    <div className="flex items-center justify-between">
                      <p className="text-xs" style={{ color: t.muted }}>{story.is_anonymous ? "Anonimo" : story.author_name || "Anonimo"}</p>
                      <button onClick={() => handleHelped(story.story_id)} className="flex items-center gap-2 text-xs px-4 py-2 rounded-full transition-all duration-500 hover:-translate-y-0.5" style={{ border: `1px solid ${t.accent}30`, color: t.accent }} data-testid={`helped-btn-${i}`}>
                        <Heart className="w-3.5 h-3.5" /> Mi ha aiutato {story.helped_count > 0 && `(${story.helped_count})`}
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* ============ 7. PERSONALIZATION ============ */}
      <section className="px-6 md:px-12 lg:px-24 py-24 lg:py-32" data-testid="personalization-section">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger} className="max-w-3xl mx-auto text-center">
          <motion.div variants={fadeUp}>
            <p className="text-sm tracking-[0.2em] uppercase mb-4" style={{ color: t.lilac }}>Il tuo spazio</p>
            <h2 className="text-3xl sm:text-4xl mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Crea la tua atmosfera</h2>
            <p className="text-base mb-12" style={{ color: t.muted }}>Scegli l'ambiente che ti fa sentire al sicuro</p>
          </motion.div>
          <motion.div variants={fadeUp} className="flex justify-center gap-6">
            {Object.entries(THEMES).map(([key, th]) => {
              const ThIcon = th.icon;
              const isActive = theme === key;
              return (
                <button key={key} onClick={() => setTheme(key)} className="flex flex-col items-center gap-3 px-8 py-6 rounded-3xl transition-all duration-700" style={{ background: isActive ? `${th.accent}15` : "transparent", border: `2px solid ${isActive ? th.accent : `${t.text}10`}`, transform: isActive ? "scale(1.05)" : "scale(1)" }} data-testid={`theme-${key}`}>
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: `${th.accent}20` }}>
                    <ThIcon className="w-5 h-5" style={{ color: th.accent }} />
                  </div>
                  <span className="text-sm font-medium">{th.label}</span>
                </button>
              );
            })}
          </motion.div>
        </motion.div>
      </section>

      {/* ============ 8. MEDICAL DISCLAIMER ============ */}
      <section className="px-6 md:px-12 lg:px-24 py-16" data-testid="disclaimer-section">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }} variants={fadeUp} className="max-w-3xl mx-auto">
          <div className="rounded-3xl p-8 md:p-10 text-center" style={{ background: `${t.softBg}60`, border: `1px solid ${t.text}06` }}>
            <Info className="w-6 h-6 mx-auto mb-4" style={{ color: t.lilac }} strokeWidth={1.5} />
            <p className="text-sm leading-relaxed" style={{ color: t.muted, fontFamily: "'Playfair Display', serif" }}>
              <em>
                I contenuti, le storie, le riflessioni e i suggerimenti presenti su ODR Stories hanno uno scopo esclusivamente informativo, divulgativo e di supporto emotivo.
                Non sostituiscono in alcun modo il parere, la diagnosi o il trattamento di un medico, psicologo o professionista sanitario qualificato.
              </em>
            </p>
            <p className="text-sm leading-relaxed mt-4" style={{ color: t.muted, fontFamily: "'Playfair Display', serif" }}>
              <em>
                Se stai attraversando un momento di difficolta, ti invitiamo a rivolgerti sempre al tuo medico di fiducia
                o a un professionista della salute mentale.
              </em>
            </p>
            <div className="mt-6 h-px w-24 mx-auto" style={{ background: `linear-gradient(to right, transparent, ${t.accent}40, transparent)` }} />
            <p className="text-xs mt-4" style={{ color: `${t.muted}80` }}>
              ODR Stories — Un progetto di ODR Academy
            </p>
          </div>
        </motion.div>
      </section>

      {/* ============ 9. FINAL SECTION ============ */}
      <section className="px-6 md:px-12 lg:px-24 py-32 lg:py-48" data-testid="final-section">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger} className="max-w-2xl mx-auto text-center">
          {/* Final image */}
          <motion.div variants={fadeUp} className="mb-12">
            <div className="w-20 h-20 mx-auto rounded-full overflow-hidden shadow-lg" style={{ boxShadow: `0 8px 30px ${t.accent}30` }}>
              <img src={IMAGES.smile} alt="Sorriso e speranza" className="w-full h-full object-cover" />
            </div>
          </motion.div>

          <motion.p variants={fadeUp} className="text-2xl sm:text-3xl leading-relaxed mb-12" style={{ fontFamily: "'Playfair Display', serif" }}>
            Non devi essere forte.<br />Devi solo essere vera.<br /><span style={{ color: t.accent }}>E qui, non sei sola.</span>
          </motion.p>
          <motion.button variants={fadeUp} onClick={scrollToWriting} className="inline-flex items-center gap-3 px-10 py-4 rounded-full text-white font-medium transition-all duration-700 hover:-translate-y-1" style={{ backgroundColor: t.accent, boxShadow: `0 4px 20px ${t.accent}40` }} data-testid="final-cta-btn">
            <PenLine className="w-5 h-5" /> Scrivi. Anche solo una parola.
          </motion.button>
          <motion.p variants={fadeUp} className="mt-16 text-xs tracking-wider" style={{ color: t.muted }}>
            ODR Stories — Dove le parole diventano forza
          </motion.p>
        </motion.div>
      </section>

      {/* Keyframes */}
      <style>{`
        @keyframes float1 { 0%, 100% { transform: translate(0, 0) scale(1); } 33% { transform: translate(30px, -30px) scale(1.05); } 66% { transform: translate(-20px, 20px) scale(0.95); } }
        @keyframes float2 { 0%, 100% { transform: translate(0, 0) scale(1); } 33% { transform: translate(-40px, 20px) scale(1.05); } 66% { transform: translate(30px, -30px) scale(0.95); } }
      `}</style>
    </div>
  );
}
